import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import IconPager from '@components/IconPager';
import AgGrid from '@components/AgGrid/AgGrid';
import { renderMetadata } from './summaryListing';
import paperStackIcon from '@assets/images/paper_stack.svg';
import { convertObjectToString } from '@utils';

const DetailChangeLog = ({
  revisionHistory,
  selectedId,
  onClose,
  handlePrevious,
  handleNext
}) => {
  const [showChanged, setShowChanged] = useState(true);

  const revisionRecord = revisionHistory.find(
    i => i && i.revisionHistoryID === selectedId,
  );
  const recordIndex = revisionHistory.findIndex(
    i => i && i.revisionHistoryID === selectedId,
  );
  const source = revisionRecord || {};
  const {
    tableName,
    changeBy,
    changeDt,
    userAction,
    metaData,
    beforeValue,
    afterValue,
  } = source;

  const getData = () => {
    let rowData = [];
    if (beforeValue && beforeValue?.length !== 0) {
      Object.keys(beforeValue).forEach(i => {
        if (Array.isArray(beforeValue[i])) {
          const bLen = beforeValue[i].length;
          const aLen = afterValue[i] ? afterValue[i].length : 0;
          for (let j = 0; j < Math.max(bLen, aLen); j++) {
            rowData.push({
              dataColumn: `${i}[${j}]`,
              beforeData: beforeValue[i][j]
                ? JSON.stringify(beforeValue[i][j])
                : '',
              afterData: afterValue[i] && afterValue[i][j]
                ? JSON.stringify(afterValue[i][j])
                : '',
            });
          }
        } else {
          rowData.push({
            dataColumn: i,
            beforeData: beforeValue && convertObjectToString(beforeValue[i]),
            afterData: afterValue && convertObjectToString(afterValue[i]),
          });
        }
      });
    } else if (afterValue && afterValue?.length !== 0) {
      Object.keys(afterValue).forEach(i => {
        if (Array.isArray(afterValue[i])) {
          const bLen = beforeValue[i] ? beforeValue[i].length : 0;
          const aLen = afterValue[i].length;
          for (let j = 0; j < Math.max(bLen, aLen); j++) {
            rowData.push({
              dataColumn: `${i}[${j}]`,
              beforeData: beforeValue && beforeValue[i] && beforeValue[i][j]
                ? JSON.stringify(beforeValue[i][j])
                : '',
              afterData: afterValue[i][j]
                ? JSON.stringify(afterValue[i][j])
                : '',
            });
          }
        } else {
          rowData.push({
            dataColumn: i,
            beforeData: beforeValue && convertObjectToString(beforeValue[i]),
            afterData: afterValue && convertObjectToString(afterValue[i]),
          });
        }
      });
    } else {
      rowData = [];
    }

    return rowData.sort((a, b) =>
      a.dataColumn.toLowerCase() > b.dataColumn.toLowerCase() ? 1 : -1,
    );
  };

  const getColumnDefs = () => [
    {
      headerName: 'Data Columns',
      field: 'dataColumn',
    },
    {
      headerName: 'Before Data',
      field: 'beforeData',
      valueFormatter: ({ value }) => value
    },
    {
      headerName: 'After Data',
      field: 'afterData',
      valueFormatter: ({ value }) => value
    },
  ];

  const memoRowData = useMemo(() => {
    return showChanged
      ? getData().filter(i => i.beforeData !== i.afterData)
      : getData()
  }, [showChanged, getData])

  return (
    <Container>
      <HeaderContainer>
        <Title>
          {selectedId} | {tableName} {userAction}
        </Title>
        <Title style={{ textAlign: 'right' }}>Revision History</Title>
      </HeaderContainer>
      <MetadataContainer>
        <MetadataColumn>
          <ValueRow>
            <Label>Date</Label>
            <span>{changeDt}</span>
          </ValueRow>
          <ValueRow>
            <Label>Username</Label>
            <span>{changeBy}</span>
          </ValueRow>
        </MetadataColumn>
        <MetadataColumn>
          <ValueRow>
            <Label>Table</Label>
            <span>{tableName}</span>
          </ValueRow>
          <ValueRow>
            <Label>Action</Label>
            <span>{userAction}</span>
          </ValueRow>
        </MetadataColumn>
        <MetadataColumn>
          MetaData:
          <br />
          {renderMetadata(metaData, ', ')}
        </MetadataColumn>
      </MetadataContainer>
      <TableContainer className="ag-theme-material">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <FormControlLabel
            style={{ margin: '0 0 0 0' }}
            control={
              <Checkbox
                color="primary"
                checked={showChanged}
                onChange={() => setShowChanged(!showChanged)}
                value="showChanged"
              />
            }
            label="Show only columns that changed"
          />
          <IconPager
            currentIndex={recordIndex}
            totalCount={revisionHistory ? revisionHistory.length : 0}
            performPrevious={handlePrevious}
            performNext={handleNext}
            icon={paperStackIcon}
            style={{ paddingBottom: 5 }}
          />
        </div>
        <AgGrid
          columnDefs={getColumnDefs()}
          defaultColDef={{ resizable: true }}
          disablePreferenceSave
          autoResizeToFit
          rowData={memoRowData}
          name="revisionHistorySummary"
        />
        <Button
          color="primary"
          variant="contained"
          onClick={onClose}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 20,
            marginRight: 20,
          }}
        >
          Close
        </Button>
      </TableContainer>
    </Container>
  );
};

DetailChangeLog.propTypes = {
  revisionHistory: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  selectedId: PropTypes.number.isRequired,
  handlePrevious: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

const Container = styled.div`
  height: 100%;
  padding-bottom: 100px;
`;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  font-family: Roboto, sans-serif;
  visibility: ${({ preview }) => (preview ? 'hidden' : 'visible')};
`;

const Title = styled.h4`
  flex: ${({ flex }) => flex || 1};
  font-family: Roboto, sans-serif;
  visibility: visible;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.87);
`;

const MetadataContainer = styled.div`
  display: flex;
  margin: 20px;
`;

const MetadataColumn = styled.div`
  flex: 1;
  flex-direction: column;
  margin-right: 20px;
`;

const ValueRow = styled.div`
  display: flex;
  flex-direction: row;
  color: #333333

  > span {
    color: #38419B;
  }
`;

const Label = styled.div`
  width: 100px;
`;

const TableContainer = styled.div`
  height: calc(100vh - 400px);
  margin: 20px;
`;

export default DetailChangeLog;
